/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { costFormatter, intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import type { ModelCostsSummaryQuery } from "./__generated__/ModelCostsSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
`;

const headerCSS = css`
  margin-bottom: 24px;
`;

const bigNumberCSS = css`
  font-size: 32px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  margin-right: 8px;
`;

const subtitleCSS = css`
  font-size: 14px;
  color: var(--ac-global-text-color-500);
`;

const tableContainerCSS = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const tableCSS = css`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th {
    text-align: left;
    padding: 8px 4px;
    color: var(--ac-global-text-color-500);
    font-weight: 500;
    border-bottom: 1px solid var(--ac-global-color-grey-200);
  }

  td {
    padding: 10px 4px;
    border-bottom: 1px solid var(--ac-global-color-grey-100);
    color: var(--ac-global-text-color-900);
  }

  tr:last-child td {
    border-bottom: none;
  }

  .text-right {
    text-align: right;
  }
`;

export function ModelCostsSummary({ projectId, timeRange }: ProjectMetricViewProps) {
    const data = useLazyLoadQuery<ModelCostsSummaryQuery>(
        graphql`
      query ModelCostsSummaryQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            topModelsByCost(timeRange: $timeRange) {
              name
              costSummary(projectId: $projectId, timeRange: $timeRange) {
                total {
                  cost
                  tokens
                }
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start?.toISOString(),
                end: timeRange.end?.toISOString(),
            },
        }
    );

    const { modelData, totalCost } = useMemo(() => {
        const models = data.project?.topModelsByCost ?? [];
        let total = 0;
        const items = models.map((m) => {
            const cost = m.costSummary?.total?.cost ?? 0;
            const tokens = m.costSummary?.total?.tokens ?? 0;
            total += cost;
            return {
                name: m.name,
                tokens: tokens,
                cost: cost,
            };
        });
        return { modelData: items, totalCost: total };
    }, [data]);

    return (
        <div css={containerCSS}>
            <div css={headerCSS}>
                <span css={bigNumberCSS}>{costFormatter(totalCost)}</span>
                <span css={subtitleCSS}>Total cost</span>
            </div>
            <div css={tableContainerCSS}>
                <table css={tableCSS}>
                    <thead>
                        <tr>
                            <th>Model</th>
                            <th className="text-right">Tokens</th>
                            <th className="text-right">USD</th>
                        </tr>
                    </thead>
                    <tbody>
                        {modelData.map((item) => (
                            <tr key={item.name}>
                                <td>{item.name}</td>
                                <td className="text-right">{intShortFormatter(item.tokens)}</td>
                                <td className="text-right">{costFormatter(item.cost)}</td>
                            </tr>
                        ))}
                        {modelData.length === 0 && (
                            <tr>
                                <td colSpan={3} style={{ textAlign: "center", padding: 20, color: "var(--ac-global-text-color-300)" }}>
                                    No model cost recorded
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
